/**
 * Estilos globais do Sistema MEDICARE
 */

import { mode } from '@chakra-ui/theme-tools';

export const styles = {
  global: (props: any) => ({
    // Estilos do corpo da página
    body: {
      fontFamily: 'body',
      color: mode('gray.800', 'whiteAlpha.900')(props),
      bg: mode('gray.50', 'gray.900')(props),
      lineHeight: 'base',
      fontSize: 'md',
      // Suavização de fontes
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      // Transições suaves
      transition: 'background-color 0.2s ease-in-out',
    },
    
    // Estilos para elementos HTML básicos
    '*': {
      borderColor: mode('gray.200', 'whiteAlpha.300')(props),
      wordWrap: 'break-word',
    },
    
    // Links
    'a': {
      color: mode('medicare.500', 'medicare.300')(props),
      textDecoration: 'none',
      transition: 'color 0.2s ease-in-out',
      _hover: {
        color: mode('medicare.600', 'medicare.400')(props),
        textDecoration: 'underline',
      },
      _focus: {
        outline: '2px solid',
        outlineColor: mode('medicare.500', 'medicare.300')(props),
        outlineOffset: '2px',
      },
    },
    
    // Cabeçalhos
    'h1, h2, h3, h4, h5, h6': {
      fontFamily: 'heading',
      fontWeight: 'semibold',
      lineHeight: 'shorter',
      color: mode('gray.900', 'white')(props),
    },
    
    // Parágrafos
    'p': {
      marginBottom: '1rem',
      lineHeight: 'base',
    },
    
    // Listas
    'ul, ol': {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
    },
    
    'li': {
      marginBottom: '0.25rem',
    },
    
    // Código
    'code': {
      fontFamily: 'mono',
      fontSize: '0.875em',
      bg: mode('gray.100', 'gray.700')(props),
      color: mode('gray.800', 'gray.100')(props),
      px: '0.25rem',
      py: '0.125rem',
      borderRadius: 'sm',
    },
    
    'pre': {
      fontFamily: 'mono',
      bg: mode('gray.100', 'gray.700')(props),
      color: mode('gray.800', 'gray.100')(props),
      p: '1rem',
      borderRadius: 'md',
      overflow: 'auto',
      marginBottom: '1rem',
    },
    
    // Tabelas
    'table': {
      borderCollapse: 'collapse',
      width: '100%',
    },
    
    'th, td': {
      borderBottom: '1px solid',
      borderColor: mode('gray.200', 'gray.600')(props),
      p: '0.75rem',
      textAlign: 'left',
    },
    
    'th': {
      fontWeight: 'semibold',
      bg: mode('gray.50', 'gray.800')(props),
    },
    
    // Formulários
    'input, textarea, select': {
      fontFamily: 'body',
    },
    
    // Scrollbar customizada
    '::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    
    '::-webkit-scrollbar-track': {
      bg: mode('gray.100', 'gray.700')(props),
      borderRadius: 'md',
    },
    
    '::-webkit-scrollbar-thumb': {
      bg: mode('gray.300', 'gray.500')(props),
      borderRadius: 'md',
      _hover: {
        bg: mode('gray.400', 'gray.400')(props),
      },
    },
    
    // Seleção de texto
    '::selection': {
      bg: mode('medicare.100', 'medicare.700')(props),
      color: mode('medicare.900', 'medicare.100')(props),
    },
    
    // Placeholder
    '::placeholder': {
      color: mode('gray.400', 'gray.500')(props),
      opacity: 1,
    },
    
    // Foco acessível
    '*:focus': {
      outline: 'none',
    },
    
    // Classes utilitárias
    '.sr-only': {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: '0',
    },
    
    '.text-truncate': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    
    '.text-balance': {
      textWrap: 'balance',
    },
    
    // Animações
    '.fade-in': {
      animation: 'fadeIn 0.3s ease-in-out',
    },
    
    '.slide-in': {
      animation: 'slideIn 0.3s ease-out',
    },
    
    '.scale-in': {
      animation: 'scaleIn 0.2s ease-out',
    },
    
    // Keyframes para animações
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
    
    '@keyframes slideIn': {
      from: {
        transform: 'translateY(-10px)',
        opacity: 0,
      },
      to: {
        transform: 'translateY(0)',
        opacity: 1,
      },
    },
    
    '@keyframes scaleIn': {
      from: {
        transform: 'scale(0.95)',
        opacity: 0,
      },
      to: {
        transform: 'scale(1)',
        opacity: 1,
      },
    },
    
    // Estilos para impressão
    '@media print': {
      body: {
        bg: 'white',
        color: 'black',
      },
      '.no-print': {
        display: 'none !important',
      },
      '.print-only': {
        display: 'block !important',
      },
    },
    
    // Responsividade
    '@media (max-width: 768px)': {
      body: {
        fontSize: 'sm',
      },
      'h1': {
        fontSize: 'xl',
      },
      'h2': {
        fontSize: 'lg',
      },
      'h3': {
        fontSize: 'md',
      },
    },
    
    // Alto contraste para acessibilidade
    '@media (prefers-contrast: high)': {
      '*': {
        borderColor: mode('black', 'white')(props),
      },
      'a': {
        color: mode('blue.800', 'blue.200')(props),
        textDecoration: 'underline',
      },
    },
    
    // Redução de movimento para acessibilidade
    '@media (prefers-reduced-motion: reduce)': {
      '*': {
        animationDuration: '0.01ms !important',
        animationIterationCount: '1 !important',
        transitionDuration: '0.01ms !important',
      },
    },
  }),
};